import * as THREE from 'three';
import { Node, NodeMaterial } from 'three/webgpu';
import {
  Fn, uniform, vec2, vec3, vec4, float,
  fract, floor, sin, mix, smoothstep, clamp, min, max,
  positionLocal, cameraViewMatrix, cameraProjectionMatrix
} from 'three/tsl';

// Airship sky — direct adaptation of the splash-screen CloudsMaterial (the
// one the user liked the look of) onto a sky sphere.
//
// The splash shader takes a 2D UV (0..1 fullscreen quad) and paints a
// gradient + two cloud layers with a cheap "sun probe" sampling trick for
// lit-top / shaded-under cumulus. To use the same aesthetic on a skybox, we
// project each pixel's view direction onto a horizontal cloud plane:
//
//   cloudUV = d.xz / max(|d.y|, 0.08)
//
// This makes clouds stretch to infinity at the horizon (the 1/|d.y| blow-up)
// and foreshorten overhead / underfoot, which reads exactly like a real
// cloud deck viewed from altitude. No seams, no equirectangular pinch.
//
// Everything else — 6-octave fbm, domain warp, sun-probe lit/shadow, and
// the same palette (lit/mid/shadow) — comes straight from CloudsMaterial.
export class AirshipSkyMaterial extends NodeMaterial {
  readonly uTime   = uniform(0);
  // Sky gradient stops.
  //   uSkyTop    — zenith (deep cobalt)
  //   uSkyMid    — horizon (bright airy cyan)
  //   uSkyBot    — nadir/below-horizon (mid-saturation blue so the
  //                under-ship band reads as "rich blue sky", not muddy grey)
  readonly uSkyTop = uniform(new THREE.Color('#163f6d'));
  readonly uSkyMid = uniform(new THREE.Color('#7fb0ce'));
  readonly uSkyBot = uniform(new THREE.Color('#3e7aa8'));
  // Sun: direction toward the light source, matching the map's sunDir so
  // shading on other surfaces agrees.
  readonly uSunDir = uniform(new THREE.Vector3(0.5, 0.8, 0.3).normalize());

  constructor() {
    super();
    this.side = THREE.BackSide;
    this.depthWrite = false;
    this.depthFunc = THREE.LessEqualDepth;
    this.lights = false;

    // Vertex trick: strip translation from the view matrix and force clip-
    // space z = w so depth ends up at 1 — sky always renders behind
    // everything.
    this.vertexNode = Fn(() => {
      const viewPos = cameraViewMatrix.mul(vec4(positionLocal, 0));
      const clip = cameraProjectionMatrix.mul(vec4(viewPos.xyz, 1));
      return vec4(clip.xy, clip.w, clip.w);
    })();

    // ─── Noise ─────────────────────────────────────────────────────────────
    const hash2 = Fn((args: [Node<'vec2'>]) => {
      const [p] = args;
      return fract(sin(p.dot(vec2(127.1, 311.7))).mul(43758.5453));
    });

    const vnoise = Fn((args: [Node<'vec2'>]) => {
      const [p] = args;
      const i = floor(p).toVar();
      const f = fract(p).toVar();
      const u = f.mul(f).mul(float(3).sub(f.mul(2)));
      const a = hash2(i);
      const b = hash2(i.add(vec2(1, 0)));
      const c = hash2(i.add(vec2(0, 1)));
      const d = hash2(i.add(vec2(1, 1)));
      return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
    });

    // 6-octave fbm for the main cloud density fields.
    const fbm6 = Fn((args: [Node<'vec2'>]) => {
      const [p] = args;
      const q = p.toVar();
      const amp = float(0.5).toVar();
      const sum = float(0).toVar();
      for (let i = 0; i < 6; i++) {
        sum.addAssign(vnoise(q).mul(amp));
        q.assign(q.mul(2.03).add(vec2(31.7, 17.3)));
        amp.assign(amp.mul(0.52));
      }
      return sum;
    });

    // 3-octave fbm for the light probe — needs to be smooth, not detailed.
    const fbm3 = Fn((args: [Node<'vec2'>]) => {
      const [p] = args;
      const q = p.toVar();
      const amp = float(0.5).toVar();
      const sum = float(0).toVar();
      for (let i = 0; i < 3; i++) {
        sum.addAssign(vnoise(q).mul(amp));
        q.assign(q.mul(2.17).add(vec2(13.1, 23.7)));
        amp.assign(amp.mul(0.55));
      }
      return sum;
    });

    this.colorNode = Fn(() => {
      const d = positionLocal.normalize().toVar();
      // `sy` — "vertical fraction". 0 at zenith (straight up), 1 at nadir.
      // Same convention as the splash shader where sy=0 was the top of the
      // sky. Used for the gradient and the cloud altitude band.
      const sy = clamp(float(0.5).sub(d.y.mul(0.5)), 0, 1).toVar();

      // ─── Sky gradient ──────────────────────────────────────────────────
      // Top → mid → bot, same two-stop blend as CloudsMaterial so the
      // atmosphere reads continuously rather than flat blue.
      const skyA = mix(this.uSkyTop, this.uSkyMid, smoothstep(0.0, 0.6, sy));
      const sky = mix(skyA, this.uSkyBot, smoothstep(0.55, 1.0, sy));

      // ─── Project view direction onto a cloud plane (above only) ───────
      // Clouds live on a horizontal plane above the camera. Ray-trace
      // `P = d · t` to the plane at y = 1 gives `t = 1 / d.y`. For d.y ≤ 0
      // the ray misses the plane → no clouds below or at the horizon.
      // A floor on d.y (0.12) keeps the horizon-grazing rays from
      // producing a 1/tiny singularity, which was what painted vertical
      // stripes across the horizon band in the previous version.
      const cloudDy = max(d.y, float(0.12)).toVar();
      const tProj = float(1).div(cloudDy).toVar();
      const cx = d.x.mul(tProj).mul(0.55).toVar();
      const cy = d.z.mul(tProj).mul(0.55).toVar();
      const c = vec2(cx, cy).toVar();
      const t = this.uTime;

      // `aboveHorizon` fades cloud density in from the horizon upward.
      // Fade-in window (0.00 → 0.12) matches the `cloudDy` projection
      // floor so clouds reach full density exactly when the projection
      // transitions from "constant t" (stripe-safe) to "t varies with
      // d.y" (smoothly perspective-correct). No seam at the transition.
      const aboveHorizon = smoothstep(0.00, 0.12, d.y).toVar();

      // Map-space sun projection for the light probe: offset in cloud-
      // space that corresponds to "step toward the sun". Sampled well
      // back from the shading pixel so the probe reads nearby cloud
      // occlusion without drifting into a totally different noise cell.
      const sunDir = this.uSunDir.normalize();
      const sunDenom = max(sunDir.y, float(0.2));
      const sunUV = vec2(sunDir.x.div(sunDenom), sunDir.z.div(sunDenom)).mul(0.55);
      const sunOff = sunUV.normalize().mul(-0.55);

      // ─── Far layer: small-scale, slow, hazy ─────────────────────────────
      const p0 = c.mul(0.55).add(vec2(t.mul(0.015), t.mul(-0.003))).toVar();
      const w0 = vec2(
        vnoise(p0.add(vec2(5.1, 3.2))).sub(0.5),
        vnoise(p0.add(vec2(11.3, 7.8))).sub(0.5)
      ).mul(1.4);
      const f0 = fbm6(p0.add(w0)).toVar();

      // ─── Near layer: larger, faster, chunkier ───────────────────────────
      const p1 = c.mul(0.95).add(vec2(t.mul(0.045), t.mul(-0.010))).toVar();
      const w1 = vec2(
        vnoise(p1.mul(0.8).add(vec2(19.1, 3.2))).sub(0.5),
        vnoise(p1.mul(0.8).add(vec2(2.3, 27.8))).sub(0.5)
      ).mul(1.7);
      const f1 = fbm6(p1.add(w1)).toVar();

      // ─── Altitude bands ─────────────────────────────────────────────────
      // Both bands are keyed off d.y (upward tilt) so the cloud deck sits
      // low — visible right at the horizon and up to ~30° above. Peak
      // density at ~6°–12° above horizon for the near layer and ~8°–16°
      // for the far layer. Well below zenith so looking up still reveals
      // clear blue sky.
      const band0 = smoothstep(0.02, 0.14, d.y).mul(smoothstep(0.55, 0.28, d.y));
      const band1 = smoothstep(0.00, 0.08, d.y).mul(smoothstep(0.38, 0.15, d.y));

      const density0 = smoothstep(0.50, 0.70, f0).mul(band0).mul(aboveHorizon).toVar();
      const density1 = smoothstep(0.50, 0.72, f1).mul(band1).mul(aboveHorizon).toVar();

      // ─── Lighting: sun-probe trick ──────────────────────────────────────
      // Sample the cloud field a short distance TOWARD the sun. If the
      // sun-side sample is denser than us, we sit in the shadow of what's
      // above; if less dense, we're lit. Straight port from CloudsMaterial.
      const probe0 = fbm3(p0.add(w0).add(sunOff));
      const lit0 = clamp(f0.sub(probe0.mul(0.85)).mul(1.8).add(0.35), 0, 1).toVar();
      const probe1 = fbm3(p1.add(w1).add(sunOff));
      const lit1 = clamp(f1.sub(probe1.mul(0.85)).mul(1.8).add(0.30), 0, 1).toVar();

      // ─── Palette ────────────────────────────────────────────────────────
      // Same three stops as the splash shader. Lit reads near-white, mid
      // soft cyan, shadow blue-grey so undersides pick up the sky.
      // cloudLit capped at ~0.77 luminance — below the post-bloom threshold
      // (see config.render.bloomThreshold). Pure-white glowing tops from the
      // previous palette (1.00/0.98/0.94 → luminance 0.98) got picked up by
      // the bloom extract pass and halo-blurred into blowouts. This palette
      // reads as soft off-white cream, which is what cumulus actually looks
      // like from above.
      const cloudLit    = vec3(0.78, 0.77, 0.74);
      const cloudMid    = vec3(0.64, 0.70, 0.78);
      const cloudShadow = vec3(0.38, 0.52, 0.64);

      const shade = (l: Node<'float'>): Node<'vec3'> =>
        mix(cloudShadow, mix(cloudMid, cloudLit, smoothstep(0.45, 0.9, l)), smoothstep(0.0, 0.55, l));

      const col0 = shade(lit0);
      const col1 = shade(lit1);

      // Far layer blends a touch toward sky colour so distant clouds fade
      // into the atmosphere rather than looking cut-out.
      const farTint = mix(col0, sky, 0.22);

      // ─── Compose back-to-front ─────────────────────────────────────────
      const withBack = mix(sky, farTint, density0.mul(0.82));
      const withFront = mix(withBack, col1, density1);

      // ─── Horizon glow ───────────────────────────────────────────────────
      // Warm thin band where atmosphere thickens. Reduced where a cloud
      // already sits so we don't double up the brightness.
      const horizon = smoothstep(0.72, 0.92, sy).mul(float(1).sub(smoothstep(0.88, 1.00, sy)));
      const horizonTint = vec3(1.00, 0.85, 0.65);
      const horizonGlow = horizonTint.mul(horizon).mul(0.10).mul(float(1).sub(min(density0.add(density1), 1)));

      return vec4(withFront.add(horizonGlow), 1);
    })();
  }

  setTime(t: number): void { this.uTime.value = t; }
}
